import { nameAttribute } from "@/commons/RCAttribute.ts";
import { SelectionMode } from "@/DependenciesV9/DataTable/Enums/TableEnum.ts";
import { IDataTableProps } from "@/DependenciesV9/DataTable/Model.ts";
import { DataTableBaseWrapper } from "@/DependenciesV9/DataTable/Style.ts";
import {
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    shorthands,
    TableCellLayout,
    TableColumnDefinition,
} from "@fluentui/react-components";
import { TableColumnSizingOptions } from "@fluentui/react-table";
import * as React from "react";
import { useEffect, useState } from "react";

const useMyStyles = makeStyles({
  root: {
    width: "100%",
    height: "100%",
  },
  dataGrid: {
    width: "100%",
    height: "100%",

    display: "flex",
    flexDirection: "column",
    ...shorthands.overflow("hidden"),
  },
  body: {
    ...shorthands.overflow("auto"),
    height: 0,
    flexGrow: 1,
    flexShrink: 1,
  },
});

const DataTable = (props: IDataTableProps) => {
  const styles = useMyStyles();
  const refMap = React.useRef<Record<string, HTMLElement | null>>({});
  const [columns, setColumns] = useState<TableColumnDefinition<any>[]>([]);
  const [columnSizing, setColumnSizing] = useState<TableColumnSizingOptions>(
    {}
  );
  //const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setColumnsFromProps();
  }, [props.columns]);

  const setColumnsFromProps = (): void => {
    const columnsClone: TableColumnDefinition<any>[] = [];
    const columnSizingClone: TableColumnSizingOptions = {};
    (props.columns || []).forEach((columnDl) => {
      const column = createTableColumn<any>({
        columnId: columnDl.key,
        compare: () => {
          return 1;
        },
        renderHeaderCell: () => {
          return columnDl.name;
        },
        renderCell: (data) => {
          return (
            <TableCellLayout truncate>
              {columnDl.onRender ? columnDl.onRender(data) : ""}
            </TableCellLayout>
          );
        },
      });
      columnSizingClone[columnDl.key] = {
        minWidth: columnDl.minWidth || undefined,
        defaultWidth:
          columnDl.maxWidth && columnDl.minWidth
            ? (columnDl.maxWidth + columnDl.minWidth) / 2
            : undefined,
        idealWidth:
          columnDl.maxWidth && columnDl.minWidth
            ? (columnDl.maxWidth + columnDl.minWidth) / 2
            : undefined,
        autoFitColumns: true,
      };
      columnsClone.push(column);
    });
    setColumns(columnsClone);
    setColumnSizing(columnSizingClone);
  };

  const listRcId = { [nameAttribute]: `dl.${props.rcName || "list"}` };

  return (
    <>
      <DataTableBaseWrapper
        className={styles.root}
        id={`HOC-wrapper-${props.rcName || "list"}`}
        //$isWarning={isWarning}
        data-is-scrollable
        $isPaging={!props.isLazyLoad && !props.hidePagination}
      >
        <DataGrid
          className={styles.dataGrid}
          items={props.data || []}
          columns={columns}
          sortable
          getRowId={(item) => item.guid}
          resizableColumns
          columnSizingOptions={columnSizing}
          subtleSelection
          selectionMode={
            props.selectionMode === SelectionMode.Single
              ? "single"
              : "multiselect"
          }
        >
          <DataGridHeader>
            <DataGridRow
              selectionCell={{
                checkboxIndicator: { "aria-label": "Select all rows" },
              }}
            >
              {({ renderHeaderCell, columnId }, dataGrid) =>
                dataGrid.resizableColumns ? (
                  <Menu openOnContext>
                    <MenuTrigger>
                      <DataGridHeaderCell
                        ref={(el) => (refMap.current[columnId] = el)}
                      >
                        {renderHeaderCell()}
                      </DataGridHeaderCell>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem
                          onClick={dataGrid.columnSizing_unstable.enableKeyboardMode(
                            columnId
                          )}
                        >
                          Keyboard Column Resizing
                        </MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                ) : (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )
              }
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<any>
            className={styles.body}
            id={`HOC-wrapper-lazy-${props.rcName || "list"}`}
            {...listRcId}
          >
            {({ item, rowId }) => (
              <DataGridRow<any>
                key={rowId}
                selectionCell={{
                  checkboxIndicator: { "aria-label": "Select row" },
                }}
              >
                {({ renderCell }) => (
                  <DataGridCell>{renderCell(item)}</DataGridCell>
                )}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </DataTableBaseWrapper>
    </>
  );
};

export default DataTable;
